import { z } from 'zod';

const MAX_RELEASE_NOTES_LENGTH = 10_000;

export const HealthzResponseSchema = z.object({
  status: z.literal('ok'),
  liveSha: z.string(),
  /** Retained for app bundles built before the broadcast announcement id (AD-1438). */
  publishedShas: z.array(z.string()),
  /**
   * Opaque id of the latest broadcast announcement. Clients compare it against
   * the last id they acknowledged, so users on a current bundle are still
   * notified. Release notes stay off this public endpoint.
   */
  announcementId: z.string().optional(),
});

export type HealthzResponse = z.infer<typeof HealthzResponseSchema>;

export const VersionAnnouncementSchema = z.object({
  id: z.string(),
  versionSha: z.string(),
  releaseNotes: z.string().nullable(),
  announcedAt: z.string(),
});

export type VersionAnnouncement = z.infer<typeof VersionAnnouncementSchema>;

/**
 * The build the web tier is currently serving, published at `/version.json`.
 * The browser reads it to tell whether reloading would actually get it newer
 * code — a question only this tier can answer, since `api` and `app` are
 * separate Deployments that roll independently (AD-2221).
 */
export const ServedVersionSchema = z.object({
  sha: z.string(),
});

export type ServedVersion = z.infer<typeof ServedVersionSchema>;

/**
 * Whether the tier serving the bundle has finished rolling out.
 *
 * A reload is only worth offering once this is true. Until then the tier's pods
 * serve a mix of builds behind one Service, so `/version.json` answers with
 * whichever pod was reached and a reload can still land on an old one — the
 * same dead-end loop under a different name (AD-2221).
 *
 * Null means the state could not be determined, which is not the same as
 * mid-rollout: clients treat only an explicit `false` as a reason to stay quiet.
 */
export const RolloutSignalSchema = z.object({
  isWebTierConverged: z.boolean().nullable(),
});

export type RolloutSignal = z.infer<typeof RolloutSignalSchema>;

/**
 * Rollout state of one Deployment, as Kubernetes reports it. `isConverged` is
 * what makes `sha` trustworthy: until a Deployment settles, its pods disagree
 * and no single SHA describes the tier.
 */
export const TierRolloutSchema = z.object({
  tier: z.string(),
  /** Deploy SHA read off the running image tag. Null when it cannot be read. */
  sha: z.string().nullable(),
  isConverged: z.boolean(),
});

export type TierRollout = z.infer<typeof TierRolloutSchema>;

export const VersionAnnouncePreviewSchema = z.object({
  liveSha: z.string(),
  alreadyAnnounced: z.boolean(),
  /** False while any tier still serves another build — announcing is refused. */
  isRolloutComplete: z.boolean(),
  /**
   * The tiers not yet serving `liveSha`. Sent already filtered so the UI names
   * what it is waiting on without restating the alignment rule.
   */
  laggingTiers: z.array(TierRolloutSchema),
});

export type VersionAnnouncePreview = z.infer<typeof VersionAnnouncePreviewSchema>;

export const VersionAnnounceBodySchema = z.object({
  notifyUsers: z.boolean(),
  releaseNotes: z.string().max(MAX_RELEASE_NOTES_LENGTH).optional(),
});

export type VersionAnnounceBody = z.infer<typeof VersionAnnounceBodySchema>;
